import { useCallback } from 'react';
import {
  InboxLocationUserSetting,
  SettingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { InboxView, ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { RelatedType, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import {
  EntityType,
  ThreadReadStatus as UnifiedThreadReadStatus,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { SMSDataSharedTypes, SMSDataSharedUtils, SMSDataV3, SMSDataV4 } from '@frontend/api-sms-data';
import { SMSSendV3 } from '@frontend/api-sms-send';
import { filterLocationIdsByACLs, getUser } from '@frontend/auth-helpers';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { notificationSounds, useSmsMessageNotification } from '@frontend/notifications';
import { formatPhoneNumber, formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { ThreadSendingAreaUtils } from '@frontend/thread-sending-area';
import { GetWeavePopNotificationByType } from '@frontend/types';
import { router } from '@frontend/weave-router';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

export const useSMSWSHandler = () => {
  const { t } = useTranslation('notifications');
  const user = getUser();
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const locationIdsWithACL = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });
  const { upsertSMS, updateSMS, updateThreadsRepliedStatus } = SMSDataV3._QueryUpdaters.useQueryUpdaters();
  const smsV4QueryUpdaters = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const getUnifiedThreadEnabled = SMSDataSharedUtils.useGetUnifiedThreadEnabled();
  const { updateScheduledSms } = ManualSmsScheduledV1._QueryUpdaters.useQueryUpdaters();
  const { incrementThreadUnreadCount } = useMiniChatShallowStore('incrementThreadUnreadCount');
  const unknownNumberString = t('Unknown');
  const { getFeatureFlagValue, featureFlags } = useAppFlagStore();
  const { onClick: onView } = useMessageAction();

  // Use the location phones hook to get display names
  const {
    data: { allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones({
    locationIds: selectedLocationIds,
  });

  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const mapKnownThreadReadStatusToUnifiedThreadReadStatus = (status: string): UnifiedThreadReadStatus => {
    switch (status) {
      case SMSDataSharedTypes.KnownThreadStatus.READ:
        return UnifiedThreadReadStatus.THREAD_READ_STATUS_READ;
      case SMSDataSharedTypes.KnownThreadStatus.NEW:
        return UnifiedThreadReadStatus.THREAD_READ_STATUS_NEW;
      case SMSDataSharedTypes.KnownThreadStatus.ERROR:
        return UnifiedThreadReadStatus.THREAD_READ_STATUS_ERROR;
      default:
        return UnifiedThreadReadStatus.THREAD_READ_STATUS_UNSPECIFIED;
    }
  };

  // Helper function to get location phone display name
  const getLocationPhoneDisplayName = useCallback(
    (locationPhone: string): string | undefined => {
      if (!locationPhone) return undefined;

      const formattedLocationPhone = formatPhoneNumberE164(locationPhone);
      const matchingLocationPhone = allLocationPhones.find((lp) => lp.locationPhone === formattedLocationPhone);
      return matchingLocationPhone?.displayName;
    },
    [allLocationPhones]
  );
  const v4ThreadCountUpdater = useCallback(
    async ({
      threadIds,
      locationId,
      newStatuses,
    }: {
      threadIds: string[];
      locationId: string;
      newStatuses: SMSDataSharedTypes.CountThreadsStatusUpdate[];
    }) => {
      newStatuses.forEach((newStatus) => {
        smsV4QueryUpdaters.updateThreadCounts({
          threadIds,
          locationId,
          newStatus,
          isOptimistic: false,
        });
      });
    },
    [smsV4QueryUpdaters]
  );
  const sendMutation = SMSSendV3.Mutations.useSendMutation();

  const { create: createNotification } = useSmsMessageNotification({
    onRespond: (notification, response) => {
      return sendMutation.mutateAsync({
        locationId: notification.payload.locationId,
        personPhone: notification.payload.number,
        body: response,
        media: [],
        programSlugId: 'manual-messages',
        personId: notification.payload.personId,
        createdBy: user?.userID,
        locationPhone: notification.payload.locationPhone,
        messageType: ThreadSendingAreaUtils.getMessageTypeFromRelatedIds([]),
      });
    },
    onView: (notification) => {
      const currentWidth = document.documentElement.clientWidth;
      const isMobile = currentWidth < breakpoints.xsmall.max;
      const inboxIsOpen = router.state.location.pathname.includes('/messages');
      onView({
        threadId: notification.payload.threadId,
        threadGroupId: notification.payload.locationId,
        personPhone: notification.payload.number === unknownNumberString ? undefined : notification.payload.number,
        personId: notification.payload.personId,
        locationPhone: notification.payload.locationPhone,
        groupIds: [notification.payload.locationId],
        openInInbox: isMobile && inboxIsOpen ? true : undefined,
      });
    },
  });
  const shouldQueryMessagingSettings = getFeatureFlagValue({
    flagName: 'nwx:messaging-user-settings-sms-messages',
    locationIds: locationIdsWithACL,
    strategy: 'ANY',
  });
  const defaultMessagingSettingsQuery = MessagingSettingsV1.Queries.useTypesQuery(
    {},
    {
      enabled: shouldQueryMessagingSettings,
      select: (data) => {
        const defaultSetting = data.types.find(
          (item) => item.settingType === SettingType_Enum.INBOX_LOCATION_USER_SETTING
        )?.defaultSetting;
        if (defaultSetting && 'inboxLocationUserSetting' in defaultSetting) {
          return defaultSetting.inboxLocationUserSetting;
        }
        return undefined;
      },
    }
  );
  const messagingSettingsQuery = MessagingSettingsV1.Queries.useListQuery(
    {
      settingType: SettingType_Enum.INBOX_LOCATION_USER_SETTING,
      locationIds: selectedLocationIds,
      orgId: selectedOrgId,
      userId: user?.userID ?? '',
    },
    {
      enabled: shouldQueryMessagingSettings && !!user?.userID,
      select: (data) => {
        return selectedLocationIds.reduce<Record<string, InboxLocationUserSetting>>((acc, locationId) => {
          const matchingSetting = data.settings.find((item) => item.locationId === locationId);

          // If no setting has been configured, there will be no setting for that location in the list
          // So we will populate that location's setting with the default if it's not in the list
          if (!matchingSetting || !('inboxLocationUserSetting' in matchingSetting.setting)) {
            acc[locationId] =
              defaultMessagingSettingsQuery.data ??
              MessagingSettingsV1.Constants.DEFAULT_SETTINGS.inboxLocationUserSetting;
            return acc;
          }

          acc[locationId] = matchingSetting.setting.inboxLocationUserSetting;
          return acc;
        }, {});
      },
    }
  );
  const messagingUserSettingsQuery = MessagingSettingsV1.Queries.useGetQuery(
    {
      settingType: SettingType_Enum.INBOX_USER_SETTING,
      userId: user?.userID ?? '',
      orgId: selectedOrgId,
    },
    {
      enabled: shouldQueryMessagingSettings && !!user?.userID,
      select: (data) => {
        if ('inboxUserSetting' in data.setting) {
          return data.setting.inboxUserSetting;
        }
        return undefined;
      },
    }
  );

  const handleNotification = useCallback<typeof createNotification>(
    (payload, options) => {
      const locationId = payload.payload.locationId;
      const newSettingsShouldBeChecked = !!featureFlags[locationId]?.get('nwx:messaging-user-settings-sms-messages')
        ?.value;
      const soundId =
        notificationSounds.find(
          (sound) => sound.name.toLowerCase() === messagingUserSettingsQuery.data?.notificationSound.toLowerCase()
        )?.id ?? '';

      const isAppointmentConfirmationMessage =
        payload.payload.threadStatus === SMSDataSharedTypes.KnownThreadStatus.READ;
      const messagingSettings = messagingSettingsQuery.data?.[locationId];
      const shouldNotify = newSettingsShouldBeChecked
        ? isAppointmentConfirmationMessage
          ? !!messagingSettings?.enableMessageNotificationsForConfirmations
          : !!messagingSettings?.enableMessageNotifications
        : true;

      const title =
        `${payload.payload.firstName || ''} ${payload.payload.lastName || ''}`.trim() ||
        formatPhoneNumber(payload.payload.number);

      const resolvedOptions: typeof options = {
        ...options,
        os: {
          ...options?.os,
          title,
          body: payload.payload.body,
        },
        // Overriding the normal notification settings if using the new messaging settings instead
        force: newSettingsShouldBeChecked,
        soundId: newSettingsShouldBeChecked ? soundId : undefined,
      };

      if (shouldNotify) {
        createNotification(payload, resolvedOptions);
      }
    },
    [createNotification, featureFlags, messagingUserSettingsQuery.data?.notificationSound, messagingSettingsQuery.data]
  );
  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.SMS>>(
    async (event) => {
      const { params } = event;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!params.weaveNumber && !checkUserLocationPhoneAccess(params.weaveNumber)) {
        return;
      }

      const newSMS = { ...SMSDataSharedUtils.convertWSPayloadToSMS(params), senderType: params.senderType };
      const shouldProcessSilently = !!params.processSilently;
      const shouldProcessQuietly = !!params.processQuietly;

      // TODO: add http_required to schema types to deal with all the nullish coalescence here
      switch (params.action) {
        case 'inbound':
          {
            const locationPhone = params.weaveNumber ?? '';
            const locationId = params.locationId || '';
            const locationPhoneDisplayName = getLocationPhoneDisplayName(locationPhone);

            if (!shouldProcessSilently && !shouldProcessQuietly) {
              handleNotification({
                id: String(event.id),
                type: 'sms-message-new',
                payload: {
                  body: params.body || '',
                  number: params.patientNumber || params.phoneMobile || unknownNumberString,
                  personId: params.PersonID || '',
                  threadId: params.threadIds?.[0] || '',
                  departmentId: params.departmentId || '',
                  locationPhone,
                  locationPhoneDisplayName,
                  locationId,
                  firstName: params.firstName || '',
                  lastName: params.lastName || '',
                  threadStatus: params.threadStatus || '',
                },
                timestamp: Date.now(),
                state: {
                  paused: false,
                  status: 'unread',
                },
                flags: {
                  shouldAllowReply: true,
                },
              } satisfies GetWeavePopNotificationByType<'sms-message-new'>);
            }

            if (newSMS) {
              if (getUnifiedThreadEnabled([newSMS.locationId])) {
                await unifiedThreadUpdater.upsertThreadItem({
                  matchValues: {
                    threadId: newSMS.threadId,
                    locationId: newSMS.locationId,
                    threadItemId: newSMS.id,
                    entityType: EntityType.ENTITY_TYPE_SMS,
                  },
                  updater: () => {
                    return {
                      smsMessage: newSMS,
                    };
                  },
                  isOptimistic: false,
                  preserveStatus: shouldProcessQuietly,
                  getThreadOnly: shouldProcessSilently,
                });
                if (!shouldProcessSilently) {
                  await unifiedThreadUpdater.upsertUnifiedThread({
                    matchValues: {
                      threadId: newSMS.threadId,
                      locationId: newSMS.locationId,
                    },
                    updater: (oldThread) => {
                      const updatedThread = oldThread
                        ? SMSDataV4._QueryUpdaters.Utils.upsertThreadItemOntoUnifiedThread(oldThread, {
                            smsMessage: newSMS,
                          })
                        : undefined;
                      if (!updatedThread) return undefined;
                      if (shouldProcessQuietly) return updatedThread;
                      const newStatus: string = params.threadStatus || updatedThread.status;
                      return {
                        ...updatedThread,
                        status: mapKnownThreadReadStatusToUnifiedThreadReadStatus(newStatus),
                        actionable:
                          newStatus === SMSDataSharedTypes.KnownThreadStatus.NEW ||
                          newStatus === SMSDataSharedTypes.KnownThreadStatus.ERROR,
                      };
                    },
                    isOptimistic: false,
                    preserveStatus: shouldProcessQuietly,
                  });
                }
              } else {
                upsertSMS({
                  sms: newSMS,
                });
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId: newSMS.threadId,
                    locationId: newSMS.locationId,
                  },
                  updater: (thread) => {
                    const updatedThread = thread
                      ? SMSDataV4._QueryUpdaters.Utils.upsertSmsOntoThread(thread, newSMS)
                      : undefined;
                    if (!updatedThread) return undefined;
                    const newStatus: string = params.threadStatus || updatedThread.status;
                    return {
                      ...updatedThread,
                      status: newStatus,
                      actionable:
                        newStatus === SMSDataSharedTypes.KnownThreadStatus.NEW ||
                        newStatus === SMSDataSharedTypes.KnownThreadStatus.ERROR,
                    };
                  },
                  isOptimistic: false,
                });
              }

              // Wait for classification to finish before refetching the count
              // TODO: remove this in favor of status update event once classification BE project is done
              setTimeout(() => {
                smsV4QueryUpdaters.invalidateQueries({
                  endpointName: 'CountThreads',
                  queryFilters: {
                    queryKey: smsV4QueryUpdaters.getQueryKey<'CountThreads'>({
                      endpointName: 'CountThreads',
                      request: {
                        filter: {
                          inboxView: InboxView.ACTIONABLE,
                        },
                      },
                    }),
                    exact: false,
                    predicate: ({ queryKey }) => {
                      return !!queryKey[2]?.locationIds?.includes(newSMS.locationId);
                    },
                  },
                });
                smsV4QueryUpdaters.invalidateQueries({
                  endpointName: 'CountThreads',
                  queryFilters: {
                    exact: false,
                    predicate: ({ queryKey }) => {
                      const request = queryKey[2];
                      return (
                        !!request?.locationIds?.includes(newSMS.locationId) &&
                        !!request.filter?.statuses?.includes(ThreadReadStatus.NEW)
                      );
                    },
                  },
                });
              }, 1000);
            }
            const threadId = params.threadIds?.[0] || '';
            if (threadId && params.threadStatus === ThreadReadStatus.NEW && !shouldProcessQuietly) {
              incrementThreadUnreadCount(threadId);
            }
          }
          break;
        case 'outbound':
          if (newSMS) {
            if (getUnifiedThreadEnabled([newSMS.locationId])) {
              await unifiedThreadUpdater.upsertThreadItem({
                matchValues: {
                  threadId: newSMS.threadId,
                  locationId: newSMS.locationId,
                  threadItemId: newSMS.id,
                  entityType: EntityType.ENTITY_TYPE_SMS,
                },
                updater: (_threadItem) => {
                  return {
                    smsMessage: newSMS,
                  };
                },
                isOptimistic: false,
                getThreadOnly: shouldProcessSilently,
              });
              if (!shouldProcessSilently) {
                await unifiedThreadUpdater.upsertUnifiedThread({
                  matchValues: {
                    threadId: newSMS.threadId,
                    locationId: newSMS.locationId,
                  },
                  updater: (oldThread) =>
                    oldThread
                      ? SMSDataV4._QueryUpdaters.Utils.upsertThreadItemOntoUnifiedThread(oldThread, {
                          smsMessage: newSMS,
                        })
                      : undefined,
                  isOptimistic: false,
                });
              }
            } else {
              upsertSMS({
                sms: newSMS,
              });
              smsV4QueryUpdaters.upsertThread({
                matchValues: {
                  threadId: newSMS.threadId,
                  locationId: newSMS.locationId,
                },
                updater: (thread) =>
                  thread ? SMSDataV4._QueryUpdaters.Utils.upsertSmsOntoThread(thread, newSMS) : undefined,
                isOptimistic: false,
              });
            }

            // Update scheduled messages that have been sent in cache
            params.relatedIds?.forEach((relatedId) => {
              if (relatedId.type !== RelatedType.RELATED_TYPE_SCHEDULED_MESSAGE_ID) return;
              updateScheduledSms({
                matchValues: {
                  id: relatedId.id,
                  locationId: newSMS.locationId,
                  threadId: newSMS.threadId,
                },
                newValues: {
                  status: OutboundMessageStatus.SENT,
                },
              });
            });
          }
          break;
        case 'inboundThreadUpdate':
          if (params.locationId) {
            const newSms = SMSDataSharedUtils.convertWSPayloadToSMS(params);
            if (newSms) {
              if (getUnifiedThreadEnabled([newSms.locationId])) {
                await unifiedThreadUpdater.upsertThreadItem({
                  matchValues: {
                    threadId: newSMS.threadId,
                    locationId: newSMS.locationId,
                    threadItemId: newSMS.id,
                  },
                  updater: () => {
                    return {
                      smsMessage: newSMS,
                    };
                  },
                  isOptimistic: false,
                  preserveStatus: shouldProcessQuietly,
                  getThreadOnly: shouldProcessSilently,
                });
                if (!shouldProcessSilently) {
                  await unifiedThreadUpdater.upsertUnifiedThread({
                    matchValues: {
                      threadId: newSMS.threadId,
                      locationId: newSMS.locationId,
                    },
                    updater: (oldThread) => {
                      const updatedThread = oldThread
                        ? SMSDataV4._QueryUpdaters.Utils.upsertThreadItemOntoUnifiedThread(oldThread, {
                            smsMessage: newSMS,
                          })
                        : undefined;
                      if (!updatedThread) return undefined;
                      if (shouldProcessQuietly) return updatedThread;
                      const newStatus: string = params.threadStatus || updatedThread.status;
                      return {
                        ...updatedThread,
                        status: mapKnownThreadReadStatusToUnifiedThreadReadStatus(newStatus),
                        actionable:
                          newStatus === SMSDataSharedTypes.KnownThreadStatus.NEW ||
                          newStatus === SMSDataSharedTypes.KnownThreadStatus.ERROR,
                      };
                    },
                    isOptimistic: false,
                    preserveStatus: shouldProcessQuietly,
                  });
                }
              } else {
                upsertSMS({
                  sms: newSms,
                });
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId: newSms.threadId,
                    locationId: newSms.locationId,
                  },
                  updater: (thread) => {
                    const updatedThread = thread
                      ? SMSDataV4._QueryUpdaters.Utils.upsertSmsOntoThread(thread, newSms)
                      : undefined;
                    if (!updatedThread) return undefined;
                    const newStatus = params.threadStatus || updatedThread.status;
                    return {
                      ...updatedThread,
                      status: newStatus,
                      actionable:
                        newStatus === SMSDataSharedTypes.KnownThreadStatus.NEW ||
                        newStatus === SMSDataSharedTypes.KnownThreadStatus.ERROR,
                    };
                  },
                  isOptimistic: false,
                });
              }
            }
          }
          break;
        case 'status': {
          const newStatus = SMSDataSharedUtils.convertStringToStatus(params.status ?? '');
          const newStatusDetails = params.statusDetails;
          if (getUnifiedThreadEnabled([params.locationId ?? ''])) {
            await unifiedThreadUpdater.upsertThreadItem({
              matchValues: {
                threadId: params.threadIds?.[0] ?? '',
                locationId: params.locationId ?? '',
                threadItemId: params.uuid ?? '',
              },
              updater: (threadItem) => {
                if (threadItem && 'smsMessage' in threadItem) {
                  const sms = threadItem.smsMessage;
                  return {
                    smsMessage: {
                      ...sms,
                      status: newStatus,
                      statusDetails: newStatusDetails || sms.statusDetails,
                    },
                  };
                }
                return threadItem;
              },
              isOptimistic: false,
              getThreadOnly: shouldProcessSilently,
            });
          } else {
            updateSMS({
              matchValues: {
                id: params.uuid ?? '',
                locationId: params.locationId,
              },
              newValues: (sms) => ({
                ...sms,
                status: newStatus,
                statusDetails: newStatusDetails || sms.statusDetails,
              }),
            });
            smsV4QueryUpdaters.upsertSms({
              matchValues: {
                smsId: params.uuid ?? '',
                threadId: params.threadIds?.[0] ?? '',
                locationId: params.locationId ?? '',
                createdAt: params.receivedAt,
              },
              updater: (oldSms) =>
                oldSms
                  ? { ...oldSms, status: newStatus, statusDetails: newStatusDetails || oldSms.statusDetails }
                  : undefined,
              isOptimistic: false,
              updateOnly: true, // Only update cached sms, don't insert on status update
            });
          }
          if (
            !shouldProcessSilently &&
            (newStatus === Status.STATUS_READ || newStatus === Status.STATUS_NEW || newStatus === Status.STATUS_ERROR)
          ) {
            v4ThreadCountUpdater({
              threadIds: params.threadIds ?? [],
              locationId: params.locationId ?? '',
              newStatuses: [
                newStatus === Status.STATUS_READ ? 'read' : newStatus === Status.STATUS_NEW ? 'unread' : 'error',
              ],
            });
          }
          break;
        }
        case 'noReplyNeeded':
          if (params.locationId && params.threadIds && params.threadIds.length > 0) {
            if (getUnifiedThreadEnabled([params.locationId])) {
              params.threadIds?.forEach((threadId) => {
                unifiedThreadUpdater.upsertUnifiedThread({
                  matchValues: {
                    threadId,
                    locationId: params.locationId ?? '',
                  },
                  matchFn: (thread) => thread.id === threadId,
                  matchStrategy: 'or',
                  updater: (thread) =>
                    thread
                      ? {
                          ...thread,
                          isReplied: true,
                        }
                      : undefined,
                  isOptimistic: false,
                });
              });
            } else {
              updateThreadsRepliedStatus({ groupIds: [params.locationId], threadIds: params.threadIds });
              params.threadIds?.forEach((threadId) => {
                smsV4QueryUpdaters.upsertThread({
                  matchValues: {
                    threadId,
                    locationId: params.locationId ?? '',
                  },
                  matchFn: (thread) => thread.id === threadId,
                  matchStrategy: 'or',
                  updater: (thread) => (thread ? { ...thread, isReplied: true } : undefined),
                  isOptimistic: false,
                });
              });
            }
          }
      }
    },
    [
      getLocationPhoneDisplayName,
      handleNotification,
      incrementThreadUnreadCount,
      smsV4QueryUpdaters,
      unknownNumberString,
      updateSMS,
      upsertSMS,
      v4ThreadCountUpdater,
      updateScheduledSms,
      getUnifiedThreadEnabled,
      unifiedThreadUpdater,
    ]
  );

  return wsEventHandler;
};

export const useSMSWSSubscription = () => {
  const handler = useSMSWSHandler();
  return useWebsocketEventSubscription(NotificationType.SMS, handler);
};
